<template>
  <claim-note
    v-if="claim.id"
    class="claim-info"
    :note="vendorCommunication"
    :claimId="claim.id"
    @save="onSaveNote"
  />
</template>

<script lang="ts">
import { computed, ComputedRef, defineComponent, PropType } from 'vue';

import { IClaim, IClaimNote, IClaimNoteOrReply, Nullable } from '@/types';
import ClaimNote from '@/components/ClaimNote.vue';
import { cloneDeep } from 'lodash';

export default defineComponent({
  components: {
    ClaimNote,
  },
  name: 'ClaimNotes',
  props: {
    claim: {
      type: Object as PropType<IClaim>,
      required: true,
    },
  },
  emits: ['save'],
  setup(props, { emit }) {
    /**
     * Call when we want to persist an `IClaimNote` or an `IClaimNoteReply` to
     * the database.
     *
     * If we know `value.id`, we can perform an `UPDATE`, otherwise it's an `INSERT`.
     * We use `value.noteType` to determine whether to update/insert a `Note` or a `NoteReply`.
     */
    function onSaveNote(value: IClaimNoteOrReply): void {
      emit('save', value);
    }

    /**
     * Gets both the `PUBLIC` and `SUPPLIER` notes, in that order.
     *
     * The UI should try to prevent adding a new `PUBLIC` note if a
     * `SUPPLIER` note already exists; that should be added as an
     * `IClaimNoteReply` instead.
     */
    function getPublicAndSupplierNotes(notes: IClaimNote[]): IClaimNote[] {
      return notes
        .filter((note: IClaimNote) => note.type === 'PUBLIC')
        .concat(notes.filter((note: IClaimNote) => note.type === 'SUPPLIER'));
    }

    /**
     * Combination of the `PUBLIC` and `SUPPLIER` notes, with all logical
     * "replies" tucked under `vendorCommunication.replies`, regardless
     * of whether they are actually an `IClaimNote` or an `IClaimReply`.
     */
    const vendorCommunication: ComputedRef<Nullable<IClaimNote>> = computed(() => {
      const notes = getPublicAndSupplierNotes(props.claim.notes || []);

      if (notes.length) {
        const firstNote = cloneDeep(notes[0]);
        firstNote.replies = notes.reduce(
          (children: IClaimNoteOrReply[], note: IClaimNote, index: number) => {
            if (index > 0) {
              children.push(note);
            }
            children.push(...(note.replies || []));
            return children;
          },
          []
        );
        return firstNote;
      } else {
        return null;
      }
    });

    return {
      vendorCommunication,
      onSaveNote,
    };
  },
});
</script>
